/**
 * Vista de detalle de una operación de recepción (transacción ANMAT a
 * confirmar). Llega del listado clickeando una card.
 *
 * Flujo de consulta (manejado en el backend):
 *   1. Si `numeroRemito` → POST a riga con `{gln, remito}`.
 *   2. Si no encontró Y hay `numeroFactura` → POST con `{gln, factura}`.
 *   3. Si tampoco encuentra → vista vacía con mensaje claro.
 *
 * Mostramos cómo se encontró (badge "Encontrado por remito X" / factura) y
 * un botón "(ver log)" que abre un modal con cada intento (request +
 * response crudo de ANMAT). Sirve para auditoría regulatoria.
 */

import { useState } from 'react';
import {
  ArrowLeft,
  Loader2,
  AlertTriangle,
  FileText,
  Receipt,
  Zap,
  CheckCircle2,
} from 'lucide-react';
import { toast } from '@/lib/notify';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useRecepcionDetalle, useConfirmarIngresoMutation } from '@/api/queries';
import { useSessionStore } from '@/stores/session';
import type { RecepcionRemito } from '@/api/contracts/recepcion-pendientes';
import type {
  RecepcionAttempt,
  RecepcionDetalleItem,
  LocalTaskFull,
} from '@/api/contracts/recepcion-detalle';

interface Props {
  /** Grupo (remito) del listado que se clickeó — para pasar remito/factura iniciales. */
  remito: RecepcionRemito;
  onVolver: () => void;
}

export function RecepcionDetalleView({ remito, onVolver }: Props) {
  const ubicacionActivaId = useSessionStore((s) => s.ubicacionActivaId);
  const sinFiltros = !remito.numeroRemito && !remito.numeroFactura;

  const { data, isLoading, error } = useRecepcionDetalle(
    sinFiltros ? null : ubicacionActivaId,
    remito.numeroRemito,
    remito.numeroFactura,
  );

  const [showLog, setShowLog] = useState(false);
  const [showLocalErrorLog, setShowLocalErrorLog] = useState(false);

  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onVolver}
        className="inline-flex items-center gap-1.5 text-xs text-gray-600 transition hover:text-everex-azul"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Volver al listado
      </button>

      {sinFiltros ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            <b>No se puede consultar el detalle</b> · esta transacción no tiene
            número de remito ni de factura asociados. Tx #
            <span className="font-mono">{remito.series[0]?.idTransaccionGlobal ?? '—'}</span>.
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-600">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          <span>Consultando ANMAT...</span>
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">
            No se pudo traer el detalle:{' '}
            {error instanceof Error ? error.message : String(error)}
          </AlertDescription>
        </Alert>
      ) : data ? (
        <>
          {/* Banner rojo arriba de todo: si un intento previo dejó series
              en error, lo primero que ve el operador es el motivo. */}
          {data.localTask && data.localTask.estado === 'error' && (
            <LocalTaskErrorBanner
              localTask={data.localTask}
              onVerLog={() => setShowLocalErrorLog(true)}
            />
          )}

          {/* Sección Confirmar — solo cuando ANMAT realmente devolvió la
              operación (no en fallback local). Si los datos vienen del
              snapshot local, ya no hay nada que confirmar: o quedó trazado
              o quedó en error y ANMAT dejó de listarlo. */}
          {data.foundBy !== null && !data.fromLocal && (
            <TrazarSection
              cantidadItems={data.items.length}
              glnId={ubicacionActivaId}
              numeroRemito={data.foundBy === 'remito' ? remito.numeroRemito : null}
              numeroFactura={data.foundBy === 'factura' ? remito.numeroFactura : null}
            />
          )}

          {/* Si la traza local quedó OK (todas las series confirmadas en
              ANMAT), mostramos el estado final en verde sin botón. */}
          {data.fromLocal && data.localTask?.estado === 'trazado' && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-700" />
              <AlertDescription className="text-xs text-green-900">
                <b>Ingreso confirmado al SNT</b> ·{' '}
                {data.localTask.totales.trazado}{' '}
                {data.localTask.totales.trazado === 1 ? 'serie' : 'series'}{' '}
                trazada(s). El stock ya fue registrado en el inventario.
              </AlertDescription>
            </Alert>
          )}

          <HeaderCard
            remito={remito}
            foundBy={data.foundBy}
            cantidadItems={data.items.length}
            onVerLog={() => setShowLog(true)}
            // Cuando ya está trazado y el data viene del fallback local, no
            // hay log nuevo de ANMAT para mostrar — ocultamos el botón.
            mostrarVerLog={!(data.fromLocal && data.localTask?.estado === 'trazado')}
          />

          {data.foundBy === null ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <b>No se encontró la operación en ANMAT</b> · se intentó
                {remito.numeroRemito ? (
                  <>
                    {' '}
                    por remito <span className="font-mono">{remito.numeroRemito}</span>
                  </>
                ) : null}
                {remito.numeroRemito && remito.numeroFactura ? ' y' : ''}
                {remito.numeroFactura ? (
                  <>
                    {' '}
                    por factura <span className="font-mono">{remito.numeroFactura}</span>
                  </>
                ) : null}
                . Revisá el log para ver la respuesta exacta de riga/ANMAT.
              </AlertDescription>
            </Alert>
          ) : (
            <SeriesTable items={data.items} />
          )}
        </>
      ) : null}

      {data && (
        <>
          <LogDialog open={showLog} onOpenChange={setShowLog} attempts={data.attempts} />
          {data.localTask && data.localTask.estado === 'error' && (
            <LocalTaskErrorLogDialog
              open={showLocalErrorLog}
              onOpenChange={setShowLocalErrorLog}
              details={data.localTask.details}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Sección "Confirmar ingreso al SNT" — gemela de la de despacho.
 *
 * Hace POST a `/api/recepcion/confirmar` que pega a riga
 * `/api/in/trazar-pendientes`. Es sincrónico (no async como egreso): la
 * respuesta llega inmediata con el resumen + ids confirmados.
 *
 * Estados visuales:
 *   - Sin intentos → botón "Confirmar ingreso" habilitado.
 *   - mutation.isPending → spinner "Confirmando..."
 *   - Resultado OK → Alert verde, botón oculto.
 *   - Resultado parcial (algunas series fallaron) → Alert amarillo, botón
 *     "Re-confirmar" para reintentar (las ya OK no se re-tocan en riga).
 *   - Error → Alert rojo + botón "Re-confirmar".
 */
function TrazarSection({
  cantidadItems,
  glnId,
  numeroRemito,
  numeroFactura,
}: {
  cantidadItems: number;
  glnId: string | null;
  numeroRemito: string | null;
  numeroFactura: string | null;
}) {
  const mutation = useConfirmarIngresoMutation();
  const [showConfirmLog, setShowConfirmLog] = useState(false);

  const handleConfirmar = () => {
    if (!glnId) {
      toast.error('No hay GLN activo. Iniciá sesión y elegí un GLN antes de confirmar.');
      return;
    }
    mutation.mutate(
      { glnId, numeroRemito, numeroFactura },
      {
        onSuccess: (data) => {
          const todasOk =
            data.status === 'success' &&
            data.totalConfirmadas === data.totalPendientesEncontrados;
          if (todasOk) {
            toast.success(
              `Ingreso confirmado · ${data.totalConfirmadas} ${
                data.totalConfirmadas === 1 ? 'serie' : 'series'
              }`,
            );
          } else {
            toast.warning(
              `Confirmación parcial · ${data.totalConfirmadas}/${data.totalPendientesEncontrados}`,
            );
          }
        },
        onError: (err) => {
          toast.error(`No se pudo confirmar: ${err.message}`);
        },
      },
    );
  };

  const data = mutation.data;
  const exitosaCompleta =
    !!data &&
    data.status === 'success' &&
    data.totalConfirmadas === data.totalPendientesEncontrados;
  const erroresAnmat = data ? extraerErroresAnmat(data.errores) : [];
  const todoFallo =
    !!data && data.totalConfirmadas === 0 && (data.status === 'error' || erroresAnmat.length > 0);
  const parcial =
    !!data && !exitosaCompleta && !todoFallo && data.totalConfirmadas > 0;

  return (
    <Card>
      <CardContent className="mt-5 space-y-3">
        {exitosaCompleta ? (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-700" />
            <AlertDescription className="text-xs text-green-900">
              <b>Ingreso confirmado al SNT</b> · {data.totalConfirmadas}{' '}
              {data.totalConfirmadas === 1 ? 'serie' : 'series'} confirmada(s).
            </AlertDescription>
          </Alert>
        ) : todoFallo ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="space-y-1 text-xs">
              <div>
                <b>ANMAT rechazó la confirmación</b> · 0 de{' '}
                {data.totalPendientesEncontrados}{' '}
                {data.totalPendientesEncontrados === 1 ? 'serie' : 'series'}{' '}
                confirmadas.
              </div>
              <ErroresAnmat errores={erroresAnmat} />
            </AlertDescription>
          </Alert>
        ) : parcial ? (
          <Alert className="border-yellow-200 bg-yellow-50">
            <AlertTriangle className="h-4 w-4 text-yellow-700" />
            <AlertDescription className="space-y-1 text-xs text-yellow-900">
              <div>
                <b>Confirmación parcial</b> · {data.totalConfirmadas} de{' '}
                {data.totalPendientesEncontrados}{' '}
                {data.totalPendientesEncontrados === 1 ? 'serie' : 'series'}{' '}
                confirmadas.
              </div>
              {erroresAnmat.length > 0 && <ErroresAnmat errores={erroresAnmat} />}
            </AlertDescription>
          </Alert>
        ) : mutation.error ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <b>Error de confirmación</b> · {mutation.error.message}
            </AlertDescription>
          </Alert>
        ) : (
          <p className="text-xs text-gray-600">
            Este ingreso todavía no fue confirmado al SNT. Hacé click en{' '}
            <b>Confirmar ingreso</b> para informar las {cantidadItems}{' '}
            {cantidadItems === 1 ? 'serie' : 'series'} a ANMAT.
          </p>
        )}

        <div className="flex items-center gap-2">
          {!exitosaCompleta && (
            <Button
              onClick={handleConfirmar}
              disabled={mutation.isPending}
              className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
            >
              {mutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Zap className="mr-2 h-4 w-4" />
              )}
              {parcial || todoFallo || mutation.error ? 'Re-confirmar' : 'Confirmar ingreso'}
            </Button>
          )}
          {mutation.isPending && (
            <span className="text-xs text-gray-600">
              Confirmando con riga/ANMAT...
            </span>
          )}
          {/* Botón "ver log" — visible cuando hay resultado (OK o error).
              Abre modal con el request y response intercambiados con riga. */}
          {data && (
            <button
              type="button"
              onClick={() => setShowConfirmLog(true)}
              className="ml-auto text-[11px] text-gray-500 underline-offset-2 transition hover:text-everex-azul hover:underline"
              title="Ver el JSON intercambiado con riga-traza al confirmar"
            >
              (ver log)
            </button>
          )}
        </div>
      </CardContent>

      {data && (
        <ConfirmLogDialog
          open={showConfirmLog}
          onOpenChange={setShowConfirmLog}
          request={data.request}
          response={data.response}
        />
      )}
    </Card>
  );
}

/**
 * Modal con el JSON intercambiado al confirmar el ingreso:
 *   - Request → riga (POST /api/in/trazar-pendientes)
 *   - Response ← riga (status, errores, ids_confirmados, etc)
 */
function ConfirmLogDialog({
  open,
  onOpenChange,
  request,
  response,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: unknown;
  response: unknown;
}) {
  const copiar = (label: string, value: unknown) => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
        toast.success(`${label} copiado al portapapeles`);
      } catch {
        toast.error('No se pudo copiar (¿el navegador bloqueó el portapapeles?)');
      }
    })();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-everex-azul">Log de confirmación</DialogTitle>
          <DialogDescription className="text-xs">
            JSON intercambiado con riga-traza al confirmar el ingreso (
            <code className="font-mono text-[10px]">POST /api/in/trazar-pendientes</code>
            ). Útil para auditoría regulatoria y diagnóstico de errores ANMAT.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 overflow-y-auto" style={{ maxHeight: '65vh' }}>
          <PayloadBloque
            label="Request → riga"
            descripcion="Body enviado al POST /api/in/trazar-pendientes"
            value={request}
            onCopiar={copiar}
          />
          <PayloadBloque
            label="Response ← riga"
            descripcion="Respuesta cruda de riga (status, errores, ids_confirmados)"
            value={response}
            onCopiar={copiar}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PayloadBloque({
  label,
  descripcion,
  value,
  onCopiar,
}: {
  label: string;
  descripcion: string;
  value: unknown;
  onCopiar: (label: string, value: unknown) => void;
}) {
  const json =
    value === null || value === undefined ? '—' : JSON.stringify(value, null, 2);
  const vacio = value === null || value === undefined;
  return (
    <div className="rounded border border-gray-200 bg-gray-50">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2">
        <div>
          <div className="text-xs font-bold text-everex-azul">{label}</div>
          <div className="text-[10px] text-gray-600">{descripcion}</div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onCopiar(label, value)}
          disabled={vacio}
          className="h-7 text-[11px]"
        >
          Copiar
        </Button>
      </div>
      <pre className="overflow-x-auto px-3 py-2 text-[11px] font-mono leading-tight text-gray-800">
        {json}
      </pre>
    </div>
  );
}

/**
 * Normaliza el campo `errores` de riga a un array `[{ codigo, descripcion }]`.
 * Riga puede mandar un objeto único `{ c_error, d_error }` (lo que vimos) o
 * un array de objetos. Otros shapes raros caen a "—".
 */
function extraerErroresAnmat(
  errores: unknown,
): Array<{ codigo: string | null; descripcion: string | null }> {
  if (!errores) return [];
  const lista = Array.isArray(errores) ? errores : [errores];
  return lista
    .filter((e): e is Record<string, unknown> => typeof e === 'object' && e !== null)
    .map((e) => ({
      codigo: typeof e.c_error === 'string' || typeof e.c_error === 'number' ? String(e.c_error) : null,
      descripcion: typeof e.d_error === 'string' ? e.d_error : null,
    }))
    .filter((e) => e.codigo || e.descripcion);
}

/** Pinta cada error de ANMAT con su código + descripción. */
function ErroresAnmat({
  errores,
}: {
  errores: Array<{ codigo: string | null; descripcion: string | null }>;
}) {
  if (errores.length === 0) return null;
  return (
    <ul className="space-y-1 pl-1">
      {errores.map((e, i) => (
        <li key={i} className="leading-tight">
          {e.codigo && (
            <span className="mr-1.5 rounded bg-red-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-red-900">
              {e.codigo}
            </span>
          )}
          <span>{e.descripcion ?? 'Error sin descripción'}</span>
        </li>
      ))}
    </ul>
  );
}

function HeaderCard({
  remito,
  foundBy,
  cantidadItems,
  onVerLog,
  mostrarVerLog = true,
}: {
  remito: RecepcionRemito;
  foundBy: 'remito' | 'factura' | null;
  cantidadItems: number;
  onVerLog: () => void;
  mostrarVerLog?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <span className="badge badge-purple">
            <b>RI</b> · Recepción ingreso
          </span>
          {remito.numeroRemito ? (
            <CardTitle className="font-mono text-everex-azul">{remito.numeroRemito}</CardTitle>
          ) : remito.numeroFactura ? (
            <CardTitle className="font-mono text-everex-azul">{remito.numeroFactura}</CardTitle>
          ) : null}
        </div>
        <p className="mt-1 text-xs text-gray-600">
          Origen: <b>{remito.razonSocialOrigen ?? '—'}</b>
          {remito.glnOrigen ? ` (${remito.glnOrigen})` : ''}
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-3">
          <FoundByBadge foundBy={foundBy} remito={remito} />
          {mostrarVerLog && (
            <button
              type="button"
              onClick={onVerLog}
              className="text-[11px] text-gray-500 underline-offset-2 transition hover:text-everex-azul hover:underline"
              title="Ver los request/response intercambiados con riga/ANMAT"
            >
              (ver log)
            </button>
          )}
          <span className="ml-auto text-xs text-gray-600">
            <b>{cantidadItems}</b> {cantidadItems === 1 ? 'serie' : 'series'}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function FoundByBadge({
  foundBy,
  remito,
}: {
  foundBy: 'remito' | 'factura' | null;
  remito: RecepcionRemito;
}) {
  if (foundBy === 'remito') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded bg-green-100 px-2 py-1 text-[11px] text-green-800">
        <FileText className="h-3.5 w-3.5" />
        Encontrado por remito <b className="font-mono">{remito.numeroRemito}</b>
      </span>
    );
  }
  if (foundBy === 'factura') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded bg-blue-100 px-2 py-1 text-[11px] text-blue-800">
        <Receipt className="h-3.5 w-3.5" />
        Encontrado por factura <b className="font-mono">{remito.numeroFactura}</b>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded bg-red-100 px-2 py-1 text-[11px] text-red-800">
      <AlertTriangle className="h-3.5 w-3.5" />
      No se encontró
    </span>
  );
}

function SeriesTable({ items }: { items: RecepcionDetalleItem[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-everex-azul text-base">
          Series a confirmar <span className="text-gray-500">({items.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
              <tr>
                <th className="px-2 py-2 text-left">#</th>
                <th className="px-2 py-2 text-left">GTIN</th>
                <th className="px-2 py-2 text-left">Producto</th>
                <th className="px-2 py-2 text-left">Lote</th>
                <th className="px-2 py-2 text-left">N° serie</th>
                <th className="px-2 py-2 text-left">Vencimiento</th>
                <th className="px-2 py-2 text-left">Evento</th>
                <th
                  className="px-2 py-2 text-left"
                  title="ID que ANMAT le asigna al confirmar la traza"
                >
                  ID Tx (traza)
                </th>
              </tr>
            </thead>
            <tbody className="text-gray-800">
              {items.map((it, i) => (
                <tr
                  key={`${it.idTransaccion ?? '?'}-${i}`}
                  className="border-t border-gray-100"
                >
                  <td className="px-2 py-2 text-gray-500">{i + 1}</td>
                  <td className="px-2 py-2 font-mono">{it.gtin ?? '—'}</td>
                  <td className="px-2 py-2">{it.nombre ?? '—'}</td>
                  <td className="px-2 py-2 font-mono">{it.lote ?? '—'}</td>
                  <td className="px-2 py-2 font-mono">{it.numeroSerial ?? '—'}</td>
                  <td className="px-2 py-2">{it.vencimiento ?? '—'}</td>
                  <td className="px-2 py-2 text-[11px]" title={it.descripcionEvento ?? undefined}>
                    {it.descripcionEvento ?? '—'}
                  </td>
                  <td className="px-2 py-2 font-mono text-[11px]">
                    {it.idTransacTraza ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function LogDialog({
  open,
  onOpenChange,
  attempts,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attempts: RecepcionAttempt[];
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-everex-azul">Log de consultas a ANMAT</DialogTitle>
          <DialogDescription className="text-xs">
            Cada intento muestra el body enviado a{' '}
            <code className="font-mono text-[10px]">
              POST /api/in/transacciones-no-confirmadas
            </code>{' '}
            y el response crudo de riga/ANMAT. Si el primero (remito) no devolvió
            resultados, el backend intenta automáticamente por factura.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto" style={{ maxHeight: '65vh' }}>
          {attempts.length === 0 ? (
            <p className="py-4 text-center text-xs text-gray-500">Sin intentos registrados.</p>
          ) : (
            attempts.map((a, i) => <AttemptBlock key={i} index={i + 1} attempt={a} />)
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AttemptBlock({ index, attempt }: { index: number; attempt: RecepcionAttempt }) {
  const copiar = (label: string, value: unknown) => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(JSON.stringify(value, null, 2));
        toast.success(`${label} copiado al portapapeles`);
      } catch {
        toast.error('No se pudo copiar (¿el navegador bloqueó el portapapeles?)');
      }
    })();
  };

  return (
    <div className="rounded border border-gray-200 bg-gray-50">
      <div className="flex items-center justify-between border-b border-gray-200 bg-white px-3 py-2">
        <div>
          <div className="text-xs font-bold text-everex-azul">
            Intento #{index} · por {attempt.by}
          </div>
          <div className="text-[10px] text-gray-600">
            HTTP {attempt.status} · {attempt.ok ? 'OK' : 'ERROR'} · {attempt.count}{' '}
            {attempt.count === 1 ? 'resultado' : 'resultados'}
          </div>
        </div>
      </div>
      <div className="space-y-2 p-3">
        <div>
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              Request
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={() => copiar('Request', attempt.request)}
            >
              Copiar
            </Button>
          </div>
          <pre className="overflow-x-auto rounded bg-white px-2 py-1.5 text-[11px] font-mono leading-tight text-gray-800">
            {JSON.stringify(attempt.request, null, 2)}
          </pre>
        </div>
        <div>
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
              Response
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px]"
              onClick={() => copiar('Response', attempt.response)}
            >
              Copiar
            </Button>
          </div>
          <pre className="overflow-x-auto rounded bg-white px-2 py-1.5 text-[11px] font-mono leading-tight text-gray-800">
            {JSON.stringify(attempt.response, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
}

/**
 * Banner rojo cuando un intento previo (anmat_task local) quedó con errores.
 * Lista las series que no se pudieron trazar con su `error_message`.
 */
function LocalTaskErrorBanner({
  localTask,
  onVerLog,
}: {
  localTask: LocalTaskFull;
  onVerLog?: () => void;
}) {
  const conError = localTask.details.filter((d) => d.estado === 'error');
  if (conError.length === 0) return null;
  return (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription className="text-xs">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span>
            <b>El último intento de traza remito falló.</b>{' '}
            {conError.length} de {localTask.totales.total} serie
            {localTask.totales.total === 1 ? '' : 's'} no pudo confirmarse en ANMAT.
            Revisá los errores y volvé a presionar <b>Confirmar ingreso</b> para reintentar.
          </span>
          {onVerLog && (
            <button
              type="button"
              onClick={onVerLog}
              className="ml-auto text-[11px] text-red-800 underline-offset-2 hover:underline"
              title="Ver el request/response crudo intercambiado con ANMAT por cada serie"
            >
              (ver log)
            </button>
          )}
        </div>
        <ul className="ml-1 space-y-1 text-[11px]">
          {conError.slice(0, 8).map((d) => (
            <li key={d.id} className="flex items-start gap-2">
              <span className="font-mono text-gray-700">
                Serie {d.numeroSerial ?? '?'}
              </span>
              <span className="text-gray-700">— {d.errorMessage ?? '(sin detalle)'}</span>
            </li>
          ))}
          {conError.length > 8 && (
            <li className="italic text-gray-600">
              … y {conError.length - 8} más.
            </li>
          )}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

/**
 * Modal con el log crudo de la traza por cada serie en error: request enviado
 * a riga + response recibido. Útil para diagnóstico regulatorio cuando hay
 * que reportar el error específico de ANMAT (c_error/d_error).
 */
function LocalTaskErrorLogDialog({
  open,
  onOpenChange,
  details,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  details: LocalTaskFull['details'];
}) {
  const conError = details.filter((d) => d.estado === 'error');
  const formatJson = (raw: string | null | undefined): string => {
    if (!raw) return '(sin contenido)';
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="text-everex-azul">
            Log de errores de traza · {conError.length}{' '}
            {conError.length === 1 ? 'serie' : 'series'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Request y response crudos del POST{' '}
            <code className="font-mono">/api/in/trazar</code> por cada serie
            que no pudo confirmarse.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 overflow-y-auto" style={{ maxHeight: '65vh' }}>
          {conError.map((d) => {
            const tieneRawLogs = Boolean(d.trazaRequest || d.trazaResponse);
            return (
              <div key={d.id} className="rounded border border-red-200 bg-red-50 p-3">
                <div className="mb-2 text-xs">
                  <b>Serie {d.numeroSerial ?? '?'}</b>
                  {d.gtin && (
                    <span className="ml-2 font-mono text-gray-600">GTIN {d.gtin}</span>
                  )}
                  {d.lote && (
                    <span className="ml-2 font-mono text-gray-600">Lote {d.lote}</span>
                  )}
                  {d.idTransaccion && (
                    <span className="ml-2 font-mono text-gray-600">
                      Tx {d.idTransaccion}
                    </span>
                  )}
                </div>
                {tieneRawLogs ? (
                  <>
                    {d.trazaRequest && (
                      <div className="mb-2">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                          Request → riga
                        </div>
                        <pre className="overflow-x-auto rounded bg-white px-2 py-1.5 text-[11px] font-mono leading-tight text-gray-800">
                          {formatJson(d.trazaRequest)}
                        </pre>
                      </div>
                    )}
                    {d.trazaResponse && (
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                          Response ← riga
                        </div>
                        <pre className="overflow-x-auto rounded bg-white px-2 py-1.5 text-[11px] font-mono leading-tight text-gray-800">
                          {formatJson(d.trazaResponse)}
                        </pre>
                      </div>
                    )}
                  </>
                ) : (
                  // Fallback para detail viejos (anteriores al flujo per-serie):
                  // no tienen request/response crudo, solo el error_message.
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                      Error reportado
                    </div>
                    <pre className="overflow-x-auto rounded bg-white px-2 py-1.5 text-[11px] font-mono leading-tight text-gray-800 whitespace-pre-wrap break-words">
                      {d.errorMessage ?? '(sin detalle)'}
                    </pre>
                    <p className="mt-1 text-[10px] italic text-gray-500">
                      Este detail se grabó con una versión anterior del sistema:
                      no se persistió el log crudo de la traza individual.
                      Reintentá el confirmar (cuando ANMAT lo vuelva a listar)
                      para capturar el JSON completo.
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
